// components/ImageUploader.jsx
import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { showLoading, hideLoading } from 'react-redux-loading-bar';
import { uploadFile } from '../../../../utilities/ImageUpload';

const ImageUploader = ({
    fieldName,
    value,
    setFieldValue,
    label = 'Select Image',
    constraints = {
        validTypes: ['image/jpeg', 'image/png', 'image/webp'],
        maxSizeMB: 3,
        minWidth: 100,
        maxWidth: 2400,
        minHeight: 100,
        maxHeight: 2400
    }
}) => {
    const dispatch = useDispatch();
    const [error, setError] = useState('');

    const validateImageFile = async (file) => {
        const { validTypes, maxSizeMB, minWidth, maxWidth, minHeight, maxHeight } = constraints;

        if (!validTypes.includes(file.type)) {
            return 'Only JPG, PNG, or WEBP images are allowed.';
        }

        if (file.size > maxSizeMB * 1024 * 1024) {
            return `File size must be less than ${maxSizeMB}MB.`;
        }

        const image = new Image();
        const objectUrl = URL.createObjectURL(file);
        image.src = objectUrl;

        return new Promise((resolve) => {
            image.onload = () => {
                URL.revokeObjectURL(objectUrl);
                if (
                    image.width < minWidth || image.width > maxWidth ||
                    image.height < minHeight || image.height > maxHeight
                ) {
                    resolve(`Image dimensions must be between ${minWidth}x${minHeight}px and ${maxWidth}x${maxHeight}px.`);
                } else {
                    resolve(null); // valid
                }
            };
            image.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                resolve('Could not read image dimensions.');
            };
        });
    };

    const handleImageChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setError('');
        dispatch(showLoading());

        const error = await validateImageFile(file);
        if (error) {
            setError(error);
            dispatch(hideLoading());
            return;
        }

        try {
            const uploadedUrl = await uploadFile(file);
            setFieldValue(fieldName, uploadedUrl);
        } catch {
            setError('Upload failed.');
        }

        dispatch(hideLoading());
    };

    const removeImage = () => {
        setFieldValue(fieldName, '');
        setError('');
    };

    return (
        <div className="form-group">
            {!value ? (
                <>
                    <div>{label}</div>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                    />
                </>
            ) : (
                <>
                    <div style={{
                        width: '100%',
                        height: '200px',
                        overflow: 'hidden',
                        margin: '0 auto',
                        position: 'relative'
                    }}>
                        <img
                            alt="preview"
                            src={value}
                            className="mt-2"
                            style={{
                                border: '1px solid #ccc',
                                objectFit: 'contain',
                                display: 'block',
                                maxWidth: '100%',
                                maxHeight: '100%',
                                margin: '0 auto',
                                boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)',
                                borderRadius: '10px'
                            }}
                        />
                    </div>
                    <div className="mt-3 d-flex justify-content-center">
                        <Button onClick={removeImage} className="btn-sm" style={{ backgroundColor: '#ea5f2d' }}>
                            Remove
                        </Button>
                    </div>
                </>
            )}

            {error && <div className="text-danger mt-2">{error}</div>}
        </div>
    );
};

export default ImageUploader;
